import type { ReactNode } from 'react';
import { useMemo, useState, useCallback } from 'react';
import {
  Card,
  Tag,
  Space,
  Select,
  Input,
  Table,
  Modal,
  Descriptions,
  Empty,
  Spin,
  Statistic,
  Row,
  Col,
  Alert,
  Button,
  message,
  Typography,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { adminPageWrap, adminAlertStyle, adminFilterCardStyle, adminContentCardStyle } from '../styles/adminPageShell';
import { useBatches } from '../hooks/useBatches';
import { useEvalRoleLabels } from '../contexts/EvalRoleLabelsContext';
import { useTalentPoolLabels } from '../contexts/TalentPoolLabelsContext';
import { LEVEL_COLOR } from '../utils/scoreColor';
import { taskStatusLabel } from '../utils/taskStatusDisplay';

const POOL_COLORS: Record<string, string> = {
  EXECUTIVE: 'red',
  MIDDLE_MGMT: 'volcano',
  BACKEND: 'blue',
  FINANCE_DATA: 'cyan',
  MARKETING_ACQUISITION: 'purple',
  SALES: 'gold',
  MEDICAL_OPS: 'green',
};

const ROLE_COLORS: Record<string, string> = {
  SELF: 'purple',
  DIRECT: 'red',
  DIAGONAL: 'volcano',
  SAME_DEPT: 'blue',
  CROSS_DEPT: 'cyan',
  SUBORDINATE: 'gold',
  PEER_LEADER: 'magenta',
  OTHER_LEADER: 'geekblue',
};

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'default',
  DRAFTED: 'processing',
  SUBMITTED: 'success',
  WITHDRAWN: 'warning',
  LOCKED: 'error',
};

interface DimensionAnchor {
  range: string;
  level: string;
  desc: string;
}

interface DimensionFull {
  code: string;
  name: string;
  desc: string;
  anchors: DimensionAnchor[];
}

interface Assignment {
  taskId: number;
  evaluatee: { id: number; name: string; position: string | null; deptPath: string | null; talentPool: string };
  roleInEval: string;
  status: string;
  template: {
    id: number;
    pool: string;
    dimensionCount: number;
    fullScore: number;
    dimensionNames: string[];
    dimensions: DimensionFull[];
    relationWeights: Record<string, number>;
  };
}

interface EvaluatorGroup {
  evaluator: { id: number; name: string; position: string | null; deptPath: string | null; talentPool: string };
  totalTasks: number;
  pending: number;
  drafted: number;
  submitted: number;
  assignments: Assignment[];
}

interface EmployeeOption {
  id: number;
  name: string;
  position?: string;
  deptPath?: string;
  talentPool: string;
}

/** 每个池「常规」推荐的 EvalRole（用于改派弹窗的「评价关系」下拉的推荐组） */
const POOL_ROLE_OPTIONS: Record<string, string[]> = {
  BACKEND: ['SELF', 'DIRECT', 'DIAGONAL', 'SAME_DEPT', 'CROSS_DEPT'],
  FINANCE_DATA: ['SELF', 'DIRECT', 'DIAGONAL', 'SAME_DEPT', 'CROSS_DEPT'],
  MARKETING_ACQUISITION: ['SELF', 'DIRECT', 'DIAGONAL', 'SAME_DEPT', 'CROSS_DEPT'],
  SALES: ['SELF', 'DIRECT', 'DIAGONAL', 'SAME_DEPT', 'CROSS_DEPT'],
  MEDICAL_OPS: ['SELF', 'DIRECT', 'DIAGONAL', 'SAME_DEPT', 'CROSS_DEPT'],
  MIDDLE_MGMT: ['SELF', 'DIRECT', 'DIAGONAL', 'SUBORDINATE', 'PEER_LEADER'],
  EXECUTIVE: ['SELF', 'DIRECT', 'OTHER_LEADER', 'SUBORDINATE'],
};

/** 全部 EvalRole 枚举值（与 prisma schema 对齐） */
const ALL_EVAL_ROLES = [
  'SELF',
  'DIRECT',
  'DIAGONAL',
  'SAME_DEPT',
  'CROSS_DEPT',
  'SUBORDINATE',
  'PEER_LEADER',
  'OTHER_LEADER',
] as const;

/** 与岗位胜任力 · 派单页「完成情况」同口径：按该评价人名下任务提交占比划分 */
function evaluatorDispatchPercent(g: EvaluatorGroup): number {
  if (g.totalTasks <= 0) return 0;
  return Math.round((g.submitted / g.totalTasks) * 100);
}

export function EvalDispatchPage() {
  const queryClient = useQueryClient();
  const { getEvalRoleLabel, enabledCodes } = useEvalRoleLabels();
  const { getTalentPoolLabel, displayOrder } = useTalentPoolLabels();
  const { batches, batchId, setBatchId } = useBatches();
  const [keyword, setKeyword] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | undefined>();
  const [poolFilter, setPoolFilter] = useState<string | undefined>();
  const [progressFilter, setProgressFilter] = useState<
    'all' | 'unfinished' | 'notStarted' | 'inProgress' | 'done'
  >('all');
  const [activeId, setActiveId] = useState<number | null>(null);

  /** 改派弹窗：把当前评价人手里这条任务转给别人 / 修改评价关系 */
  const [editTask, setEditTask] = useState<{
    task: Assignment;
    fromEvaluator: { id: number; name: string };
  } | null>(null);
  const [editPickId, setEditPickId] = useState<number | undefined>();
  const [editRolePick, setEditRolePick] = useState<string | undefined>();

  const { data: groups = [], isLoading } = useQuery<EvaluatorGroup[]>({
    queryKey: ['eval-grouped-by-evaluator', batchId],
    queryFn: () => api.get('/admin/eval-tasks/grouped-by-evaluator', { params: { batchId } }),
    enabled: !!batchId,
  });

  /** 编辑弹窗打开时拉花名册（同池排前） */
  const { data: empPage } = useQuery<{ items: EmployeeOption[] }>({
    queryKey: ['admin-employees-pick'],
    queryFn: () => api.get('/admin/employees', { params: { status: 'ACTIVE', page: 1, size: 500 } }),
    enabled: editTask !== null,
  });

  const { data: qualityTemplates = [] } = useQuery<
    Array<{ pool: string; relationWeights: Record<string, number> }>
  >({
    queryKey: ['quality-templates', batchId],
    queryFn: () => api.get('/admin/quality-templates', { params: { batchId } }),
    enabled: !!batchId,
  });

  const poolAllowedRoles = useMemo(() => {
    const map: Record<string, string[]> = { ...POOL_ROLE_OPTIONS };
    for (const t of qualityTemplates) {
      const keys = Object.keys(t.relationWeights || {});
      if (keys.length) map[t.pool] = keys;
    }
    return map;
  }, [qualityTemplates]);

  const roleFilterOptions = useMemo(
    () =>
      (enabledCodes.length ? enabledCodes : [...ALL_EVAL_ROLES]).map((r) => ({
        value: r,
        label: getEvalRoleLabel('BACKEND', r),
      })),
    [enabledCodes, getEvalRoleLabel],
  );

  const buildRoleOptions = useCallback(
    (pool: string) => {
      const recommended = poolAllowedRoles[pool] || POOL_ROLE_OPTIONS[pool] || POOL_ROLE_OPTIONS.BACKEND;
      const others = (enabledCodes.length ? enabledCodes : [...ALL_EVAL_ROLES]).filter(
        (r) => !recommended.includes(r),
      );
      const toOpt = (r: string) => ({
        value: r,
        label: `${getEvalRoleLabel(pool, r)}（${r}）`,
      });
      const groups: Array<{ label: ReactNode; options: { value: string; label: string }[] }> = [];
      if (recommended.length) {
        groups.push({
          label: <span style={{ color: '#1677ff' }}>本池常用</span>,
          options: recommended.map(toOpt),
        });
      }
      if (others.length) {
        groups.push({
          label: <span style={{ color: '#fa8c16' }}>其他角色（不在本池模板中，权重可能为 0）</span>,
          options: others.map(toOpt),
        });
      }
      return groups;
    },
    [poolAllowedRoles, enabledCodes, getEvalRoleLabel],
  );

  const reassignMutation = useMutation({
    mutationFn: (vars: { taskId: number; newEvaluatorId?: number; newRoleInEval?: string }) =>
      api.post(`/admin/eval-tasks/${vars.taskId}/reassign`, {
        newEvaluatorId: vars.newEvaluatorId,
        newRoleInEval: vars.newRoleInEval,
      }),
    onSuccess: () => {
      message.success('改派成功，已同步到「素质项评价关系」与「素质项评价进度」');
      // 同步多视图：用 prefix 失效，确保 EvalRelationsPage 和 EvalTasksPage 切换页面后看到新评价人
      queryClient.invalidateQueries({ queryKey: ['eval-grouped-by-evaluator'] });
      queryClient.invalidateQueries({ queryKey: ['eval-tasks-admin'] });
      setEditTask(null);
      setEditPickId(undefined);
      setEditRolePick(undefined);
    },
    onError: (e: any) => {
      message.error(e?.message || '改派失败');
    },
  });

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    const poolRank = (p: string) => {
      const i = displayOrder.indexOf(p);
      return i === -1 ? 999 : i;
    };
    const ordered = [...groups].sort((a, b) => {
      const c = poolRank(a.evaluator.talentPool) - poolRank(b.evaluator.talentPool);
      if (c !== 0) return c;
      return a.evaluator.name.localeCompare(b.evaluator.name, 'zh-CN');
    });
    return ordered.filter((g) => {
      if (kw && !g.evaluator.name.includes(kw)) return false;
      if (poolFilter && g.evaluator.talentPool !== poolFilter) return false;
      if (roleFilter && !g.assignments.some((a) => a.roleInEval === roleFilter)) return false;
      const pct = evaluatorDispatchPercent(g);
      if (progressFilter === 'done' && pct !== 100) return false;
      if (progressFilter === 'unfinished' && pct === 100) return false;
      if (progressFilter === 'inProgress' && (pct === 0 || pct === 100)) return false;
      if (progressFilter === 'notStarted' && pct !== 0) return false;
      return true;
    });
  }, [groups, keyword, roleFilter, poolFilter, progressFilter, displayOrder]);

  const stats = useMemo(() => {
    const totalEvaluators = groups.length;
    const totalTasks = groups.reduce((s, g) => s + g.totalTasks, 0);
    const totalSubmitted = groups.reduce((s, g) => s + g.submitted, 0);
    return { totalEvaluators, totalTasks, totalSubmitted };
  }, [groups]);

  const active = useMemo(() => groups.find((g) => g.evaluator.id === activeId), [groups, activeId]);

  return (
    <div style={adminPageWrap}>
      <div className="tg-page-title">评价派单</div>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16, maxWidth: 960 }}>
        从评价人视角查看派单量、关系分布与完成进度；支持按批次、姓名、人才池、评价关系与完成情况（与「岗位胜任力 · 派单」一致）筛选。
      </Typography.Paragraph>

      <Alert
        type="info"
        showIcon
        style={adminAlertStyle}
        message="按「评价人」视角看派单"
        description="切换批次和筛选。下方表格中「关系」按被评价人所在池显示为你定义的称呼（如高层：其他领导、其他领导/下属）。展开详情可看到该池下的维度、权重与每档 0–7 分行为描述；员工填表时亦逐条展示。派单以 CSV+导入结果为准，若与组织汇报链不一致，请在花名册维护「直属上级」后重新导入评价关系。"
      />

      <Card style={adminFilterCardStyle} styles={{ body: { paddingBottom: 12 } }}>
        {/* 部署校验：scripts/verify-frontend-build.sh 会在 PC dist 中 grep 此 id */}
        <span id="tg-eval-dispatch-progress-filter-marker" style={{ display: 'none' }} aria-hidden />
        {/* 与「岗位胜任力 · 派单」同形态：单行筛选，完成情况紧邻批次 */}
        <Space wrap size={12}>
          <span style={{ color: '#595959' }}>批次：</span>
          <Select
            placeholder="选择批次"
            style={{ width: 220 }}
            options={batches.map((b) => ({ label: b.name, value: b.id }))}
            value={batchId}
            onChange={setBatchId}
          />
          <span style={{ marginLeft: 8, color: '#595959' }}>完成情况：</span>
          <Select
            value={progressFilter}
            onChange={setProgressFilter}
            style={{ width: 160 }}
            options={[
              { value: 'all', label: '全部' },
              { value: 'unfinished', label: '未完成' },
              { value: 'notStarted', label: '未开始' },
              { value: 'inProgress', label: '进行中' },
              { value: 'done', label: '已完成' },
            ]}
          />
          <span style={{ marginLeft: 8, color: '#595959' }}>评价人：</span>
          <Input.Search
            placeholder="按姓名搜索"
            allowClear
            style={{ width: 220 }}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <span style={{ marginLeft: 8, color: '#595959' }}>人才池：</span>
          <Select
            placeholder="全部"
            allowClear
            style={{ width: 160 }}
            value={poolFilter}
            onChange={setPoolFilter}
            options={displayOrder.map((v) => ({ value: v, label: getTalentPoolLabel(v) }))}
          />
          <span style={{ marginLeft: 8, color: '#595959' }}>评价关系：</span>
          <Select
            placeholder="全部"
            allowClear
            style={{ width: 200 }}
            value={roleFilter}
            onChange={setRoleFilter}
            options={roleFilterOptions}
          />
        </Space>
        <Row gutter={16} style={{ marginTop: 12 }}>
          <Col span={6}><Statistic title="评价人总数" value={stats.totalEvaluators} /></Col>
          <Col span={6}><Statistic title="派单总条数" value={stats.totalTasks} /></Col>
          <Col span={6}>
            <Statistic
              title="已提交条数"
              value={stats.totalSubmitted}
              suffix={stats.totalTasks ? `/ ${stats.totalTasks}` : ''}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="完成率"
              value={stats.totalTasks ? Math.round((stats.totalSubmitted / stats.totalTasks) * 1000) / 10 : 0}
              suffix="%"
            />
          </Col>
        </Row>
      </Card>

      {isLoading ? (
        <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
      ) : filtered.length === 0 ? (
        <Empty description="没有匹配的派单。请先在「素质项评价进度」/「批次详情」生成任务，或导入评价关系表。" />
      ) : (
        <Table<EvaluatorGroup>
          rowKey={(g) => g.evaluator.id}
          dataSource={filtered}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          bordered
          style={{ ...adminContentCardStyle, overflow: 'hidden' }}
          columns={[
            {
              title: '评价人',
              dataIndex: ['evaluator', 'name'],
              width: 140,
              render: (_v, g) => (
                <div>
                  <div><b>{g.evaluator.name}</b></div>
                  <div style={{ color: '#888', fontSize: 12 }}>{g.evaluator.position || '-'}</div>
                </div>
              ),
            },
            {
              title: '所属部门',
              dataIndex: ['evaluator', 'deptPath'],
              ellipsis: true,
              render: (v) => <span style={{ color: '#666', fontSize: 12 }}>{v || '-'}</span>,
            },
            {
              title: '本人池',
              dataIndex: ['evaluator', 'talentPool'],
              width: 110,
              render: (v) => v ? <Tag color={POOL_COLORS[v] || 'default'}>{getTalentPoolLabel(v)}</Tag> : '-',
            },
            {
              title: '需评价人数',
              dataIndex: 'totalTasks',
              width: 110,
              align: 'right',
              sorter: (a, b) => a.totalTasks - b.totalTasks,
              defaultSortOrder: 'descend',
              render: (v) => <b>{v}</b>,
            },
            {
              title: '关系分布',
              key: 'roles',
              render: (_v, g) => {
                const roleCount = new Map<string, { role: string; n: number }>();
                g.assignments.forEach((a) => {
                  const label = getEvalRoleLabel(a.template.pool, a.roleInEval);
                  const prev = roleCount.get(label);
                  if (prev) roleCount.set(label, { role: prev.role, n: prev.n + 1 });
                  else roleCount.set(label, { role: a.roleInEval, n: 1 });
                });
                return (
                  <Space wrap size={[4, 4]}>
                    {Array.from(roleCount.entries()).map(([label, { role, n }]) => (
                      <Tag key={`${label}-${role}`} color={ROLE_COLORS[role] || 'default'}>{label} {n}</Tag>
                    ))}
                  </Space>
                );
              },
            },
            {
              title: '完成进度',
              key: 'progress',
              width: 260,
              sorter: (a, b) => {
                const ra = a.totalTasks ? a.submitted / a.totalTasks : 0;
                const rb = b.totalTasks ? b.submitted / b.totalTasks : 0;
                return ra - rb;
              },
              render: (_v, g) => {
                const rate = g.totalTasks
                  ? Math.round((g.submitted / g.totalTasks) * 1000) / 10
                  : 0;
                const rateColor =
                  g.totalTasks === 0
                    ? 'default'
                    : rate === 100
                      ? 'success'
                      : rate >= 80
                        ? 'blue'
                        : rate > 0
                          ? 'orange'
                          : 'red';
                return (
                  <Space size={4} wrap>
                    <Tag color="default">待评 {g.pending}</Tag>
                    <Tag color="processing">草稿 {g.drafted}</Tag>
                    <Tag color="success">已提交 {g.submitted}</Tag>
                    <Tag color={rateColor} style={{ fontWeight: 600 }}>
                      完成率 {g.totalTasks ? `${rate}%` : '-'}
                    </Tag>
                  </Space>
                );
              },
            },
            {
              title: '操作',
              width: 100,
              render: (_v, g) => (
                <Button type="link" size="small" onClick={() => setActiveId(g.evaluator.id)}>
                  查看详情
                </Button>
              ),
            },
          ]}
        />
      )}

      <Modal
        title={
          active ? (
            <div>
              <b style={{ fontSize: 16 }}>{active.evaluator.name}</b>
              <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>{active.evaluator.position}</span>
              <Tag style={{ marginLeft: 8 }} color={POOL_COLORS[active.evaluator.talentPool] || 'default'}>
                {getTalentPoolLabel(active.evaluator.talentPool)}
              </Tag>
              <span style={{ marginLeft: 8, color: '#666', fontSize: 12 }}>共需评价 {active.totalTasks} 人</span>
            </div>
          ) : null
        }
        open={!!active}
        onCancel={() => setActiveId(null)}
        footer={null}
        width={1100}
      >
        {active && (
          <>
            <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="所属部门" span={2}>{active.evaluator.deptPath || '-'}</Descriptions.Item>
              <Descriptions.Item label="待评 / 草稿 / 已提交" span={2}>
                <Tag>待评 {active.pending}</Tag>
                <Tag color="processing">草稿 {active.drafted}</Tag>
                <Tag color="success">已提交 {active.submitted}</Tag>
              </Descriptions.Item>
            </Descriptions>

            {(() => {
              // 评价人可能跨池评（如人事经理同时评高层/中层/综合/财务池），按池去重列出所有命中的池模板
              const poolToTpl = new Map<string, Assignment['template'] & { count: number }>();
              for (const a of active.assignments) {
                const t = a.template;
                if (!t?.dimensions?.length) continue;
                const cur = poolToTpl.get(t.pool);
                if (cur) cur.count += 1;
                else poolToTpl.set(t.pool, { ...t, count: 1 });
              }
              const tpls = Array.from(poolToTpl.values());
              if (tpls.length === 0) return null;
              return (
                <>
                  {tpls.map((tpl) => {
                    const weightEntries = Object.entries(tpl.relationWeights || {})
                      .filter(([, v]) => Number(v) > 0)
                      .sort((a, b) => Number(b[1]) - Number(a[1]));
                    return (
                      <Card
                        key={tpl.id}
                        size="small"
                        title={
                          <span>
                            评分标准 ·{' '}
                            <Tag color={POOL_COLORS[tpl.pool] || 'default'}>
                              {getTalentPoolLabel(tpl.pool)}
                            </Tag>
                            <span style={{ color: '#888', fontWeight: 400, fontSize: 12 }}>
                              {tpl.dimensionCount} 维 · 满分 {tpl.fullScore}（每维 0-7 分） · 该池命中 {tpl.count} 条
                            </span>
                          </span>
                        }
                        style={{ marginBottom: 12 }}
                      >
                        <div style={{ marginBottom: 12 }}>
                          <b style={{ fontSize: 13 }}>关系权重（评价人贡献占比）：</b>
                          <Space wrap size={[6, 6]} style={{ marginLeft: 8 }}>
                            {weightEntries.length === 0 ? (
                              <span style={{ color: '#999' }}>未配置</span>
                            ) : (
                        weightEntries.map(([role, weight]) => (
                          <Tag key={role} color={ROLE_COLORS[role] || 'default'}>
                            {getEvalRoleLabel(tpl.pool, role)} {weight}%
                          </Tag>
                        ))
                            )}
                          </Space>
                        </div>
                        <div style={{ display: 'grid', gap: 10 }}>
                          {tpl.dimensions.map((d, idx) => (
                            <div
                              key={d.code || d.name || idx}
                              style={{
                                padding: 10,
                                border: '1px solid #f0f0f0',
                                borderRadius: 6,
                                background: '#fafafa',
                              }}
                            >
                              <div style={{ fontWeight: 600, fontSize: 13 }}>
                                {idx + 1}. {d.name}
                              </div>
                              {d.desc && (
                                <div style={{ color: '#666', fontSize: 12, margin: '4px 0 8px' }}>{d.desc}</div>
                              )}
                              {d.anchors?.length > 0 && (
                                <div style={{ display: 'grid', gap: 4 }}>
                                  {d.anchors.map((a) => (
                                    <div key={a.range} style={{ fontSize: 12, lineHeight: 1.6, color: '#333' }}>
                                      <Tag color={LEVEL_COLOR[a.level] || 'default'} style={{ marginRight: 6 }}>
                                        {a.range} · {a.level}
                                      </Tag>
                                      <span>{a.desc}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </Card>
                    );
                  })}
                </>
              );
            })()}

            <Table<Assignment>
              rowKey="taskId"
              size="small"
              pagination={false}
              dataSource={active.assignments}
              columns={[
                {
                  title: '被评价人',
                  width: 130,
                  render: (_v, a) => (
                    <div>
                      <div><b>{a.evaluatee.name}</b></div>
                      <div style={{ color: '#888', fontSize: 12 }}>{a.evaluatee.position || '-'}</div>
                    </div>
                  ),
                },
                {
                  title: '部门',
                  dataIndex: ['evaluatee', 'deptPath'],
                  ellipsis: true,
                  render: (v) => <span style={{ color: '#666', fontSize: 12 }}>{v || '-'}</span>,
                },
                {
                  title: '池',
                  dataIndex: ['evaluatee', 'talentPool'],
                  width: 100,
                  render: (v) => v ? <Tag color={POOL_COLORS[v] || 'default'}>{getTalentPoolLabel(v)}</Tag> : '-',
                },
                {
                  title: '关系',
                  dataIndex: 'roleInEval',
                  width: 140,
                  render: (v, a) => (
                    <Tag color={ROLE_COLORS[v] || 'default'}>{getEvalRoleLabel(a.template.pool, v)}</Tag>
                  ),
                },
                {
                  title: '评价模板 / 维度',
                  render: (_v, a) => (
                    <div>
                      <div style={{ marginBottom: 4 }}>
                        <Tag color={POOL_COLORS[a.template.pool] || 'default'}>
                          {getTalentPoolLabel(a.template.pool)}
                        </Tag>
                        <span style={{ color: '#888', fontSize: 12 }}>
                          {a.template.dimensionCount} 维 · 满分 {a.template.fullScore}
                        </span>
                      </div>
                      <div style={{ color: '#555', fontSize: 12 }}>
                        {a.template.dimensionNames.join(' · ')}
                      </div>
                    </div>
                  ),
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  width: 90,
                  render: (v) => <Tag color={STATUS_COLOR[v] || 'default'}>{taskStatusLabel(v)}</Tag>,
                },
                {
                  title: '操作',
                  width: 110,
                  render: (_v, a) => (
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        if (!active) return;
                        setEditTask({
                          task: a,
                          fromEvaluator: { id: active.evaluator.id, name: active.evaluator.name },
                        });
                        setEditPickId(undefined);
                        setEditRolePick(undefined);
                      }}
                    >
                      改派
                    </Button>
                  ),
                },
              ]}
            />
          </>
        )}
      </Modal>

      {/* === 改派 Modal（嵌套在详情 Modal 之上）：评价人 + 评价关系都能改 === */}
      <Modal
        title="改派评价人 / 评价关系"
        open={editTask !== null}
        onCancel={() => {
          setEditTask(null);
          setEditPickId(undefined);
          setEditRolePick(undefined);
        }}
        onOk={() => {
          if (!editTask) return;
          const evChanged = editPickId != null && editPickId !== editTask.fromEvaluator.id;
          const roleChanged = editRolePick != null && editRolePick !== editTask.task.roleInEval;
          if (!evChanged && !roleChanged) {
            message.warning('未变更，请至少修改评价人或评价关系其中之一');
            return;
          }
          reassignMutation.mutate({
            taskId: editTask.task.taskId,
            newEvaluatorId: evChanged ? editPickId : undefined,
            newRoleInEval: roleChanged ? editRolePick : undefined,
          });
        }}
        okButtonProps={{ loading: reassignMutation.isPending }}
        okText="改派"
        cancelText="取消"
        destroyOnClose
        width={600}
        zIndex={1100}
      >
        {editTask && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <div style={{ color: '#888', fontSize: 12 }}>当前评价人</div>
              <Tag>{editTask.fromEvaluator.name}</Tag>
            </div>
            <div>
              <div style={{ color: '#888', fontSize: 12 }}>被评价人</div>
              <div>
                <strong>{editTask.task.evaluatee.name}</strong>
                <span style={{ color: '#999', marginLeft: 8 }}>
                  {editTask.task.evaluatee.position || '—'} · {editTask.task.evaluatee.deptPath || '—'}
                </span>
              </div>
            </div>
            <div>
              <div style={{ color: '#888', fontSize: 12 }}>评价关系</div>
              <Tag color={ROLE_COLORS[editTask.task.roleInEval] || 'default'}>
                {getEvalRoleLabel(editTask.task.template.pool, editTask.task.roleInEval)}
              </Tag>
              <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>
                taskId: {editTask.task.taskId}
              </span>
            </div>
            <div>
              <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>改派为（评价人）</div>
              <Select
                showSearch
                allowClear
                placeholder="不改的话留空；要改派则搜索员工姓名 / 职位 / 部门"
                value={editPickId}
                onChange={setEditPickId}
                style={{ width: '100%' }}
                filterOption={(input, opt) =>
                  String(opt?.label || '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={(() => {
                  const items = empPage?.items || [];
                  const targetPool = editTask.task.evaluatee.talentPool;
                  const sorted = [...items].sort((a, b) => {
                    if (a.talentPool === targetPool && b.talentPool !== targetPool) return -1;
                    if (a.talentPool !== targetPool && b.talentPool === targetPool) return 1;
                    return (a.name || '').localeCompare(b.name || '', 'zh-CN');
                  });
                  return sorted.map((e) => ({
                    label: `${e.name}${e.position ? ` · ${e.position}` : ''}${e.deptPath ? ` · ${e.deptPath}` : ''}`,
                    value: e.id,
                  }));
                })()}
              />
            </div>
            <div>
              <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>评价关系改为</div>
              <Select
                allowClear
                placeholder="不改的话留空；要改关系则选新角色（自评、直属、下属、其他领导… 全部 8 种都可选）"
                value={editRolePick}
                onChange={setEditRolePick}
                style={{ width: '100%' }}
                options={buildRoleOptions(editTask.task.template.pool)}
              />
              <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                改派会把该任务的状态重置为「待提交」并清空草稿。改派成功后，本卡片会自动从「{editTask.fromEvaluator.name}」的派单列表中消失（如改了评价人）；如改了关系，新关系会同步到「素质项评价关系」对应列。
                选了「其他角色」（不在本池模板）时，模板的关系权重可能为 0，请确认是要换关系还是要换池。
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
